import type { ToolCallStatus } from '@kanbu/schema';
import {
  isShowFormArtifact,
  showFormArtifactSchema,
  type ShowFormArtifact,
} from '@kanbu/schema/contracts';
import { ToolType } from '@kanbu/schema/enums';
import { z } from 'zod';

import { FormToolCallView } from './form-tool-call-view';
import { createToolComponent } from '../tool-registry';

export interface ClientSideFormToolProps {
  onSubmit?: (params: {
    toolName: string;
    toolCallId: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
}

const emptyArgsSchema = z.object({});

export const ClientSideFormTool = createToolComponent<
  ClientSideFormToolProps,
  typeof emptyArgsSchema,
  never,
  typeof showFormArtifactSchema
>(
  {
    schema: {
      args: emptyArgsSchema,
      artifact: showFormArtifactSchema,
    },
    validate: true,
  },
  props => {
    if (!props.artifact || !isShowFormArtifact(props.artifact)) {
      return null;
    }

    return (
      <FormToolCallView
        artifact={props.artifact}
        toolCall={props.toolCall}
        state={props.state}
        error={props.error}
        onSubmit={props.onSubmit}
      />
    );
  },
);

export function canRenderClientSideForm(artifact: unknown): boolean {
  return isShowFormArtifact(artifact);
}

export function renderClientSideFormFallback(
  props: ClientSideFormToolProps & {
    toolName: string;
    toolCallId: string;
    content?: unknown;
    artifact?: unknown;
    toolCallStatus?: ToolCallStatus;
    kind?: string;
    state: 'call' | 'call-partial' | 'call-complete' | 'result' | 'error';
    error?: string;
  },
) {
  if (props.kind !== 'form' && !isShowFormArtifact(props.artifact)) {
    return null;
  }

  const Component = ClientSideFormTool.component;

  return (
    <Component
      toolCall={{
        id: props.toolCallId,
        name: props.toolName,
        type: ToolType.Tool,
        args: {} as z.infer<typeof emptyArgsSchema>,
        index: 0,
        content: props.content,
        artifact: props.artifact as ShowFormArtifact,
        status: props.toolCallStatus,
      }}
      state={props.state}
      artifact={props.artifact as ShowFormArtifact}
      error={props.error}
      onSubmit={props.onSubmit}
    />
  );
}

export { FormArtifactView } from './form-artifact-view';
export { isShowFormArtifact } from '@kanbu/schema/contracts';
